// /recipes/:id — Admin Recipe Detail (PAGES.md §3.63)
//
// Same as POS recipe detail (§2.22) wrapped in PageContainer + ErrorBoundary.
// RESTAURANT vertical only — MANAGER+ (30). Delete: OWNER+ (50).
// A dish has one recipe at every outlet (docs/master-catalogue-plan.md §17):
// a Manager creates a dish's first recipe, only an Owner changes it.
// Traceability: FR-RECIPE-001 to FR-RECIPE-008

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  TextArea,
  Select,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  Modal,
  ConfirmDialog,
  DataTable,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { recipeService } from '@/services/recipe.service';
import type { Ingredient, RecipeUnit } from '@/services/recipe.service';
import { productService } from '@/services/product.service';
import { buildRecipeSavePlan } from './recipe-save';
import { buildMenuItemOptions } from './recipe-menu-items';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { GLASS, cn, toneClasses } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';

// ─── Icons ──────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function BeakerIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 3h6v4.5L19 20H5L9 7.5V3z" />
      <path d="M9 3h6" />
      <path d="M7 15h10" />
    </svg>
  );
}

// ─── Constants ──────────────────────────────────────────────────────────────

const UNIT_OPTIONS: { value: RecipeUnit; label: string }[] = [
  { value: 'KG', label: 'KG' }, { value: 'G', label: 'G' },
  { value: 'L', label: 'L' }, { value: 'ML', label: 'ML' },
  { value: 'PCS', label: 'PCS' }, { value: 'DOZEN', label: 'Dozen' },
];

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100, ADMIN: 80, MODERATOR: 60, OWNER: 50, MANAGER: 30, CASHIER: 5,
};


// ─── KPI ────────────────────────────────────────────────────────────────────

function KpiMini({ label, value, tone }: { label: string; value: string | number; tone: 'brand' | 'success' | 'danger' | 'violet' | 'teal' }) {
  const tc = toneClasses(tone);
  return (
    <div className={cn(GLASS, 'relative overflow-hidden p-4')}>
      <div className={`absolute inset-0 bg-gradient-to-br ${tc.accent} pointer-events-none`} />
      <p className="relative text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="relative text-xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono mt-1">{value}</p>
    </div>
  );
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface EditableIngredient {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit: RecipeUnit;
  waste_percentage: number;
  cost_per_unit: number;
}

// ─── Component ──────────────────────────────────────────────────────────────

function RecipeDetailContent() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const canDelete = !isNew && (ROLE_LEVELS[role] ?? 0) >= ROLE_LEVELS.OWNER;
  const readOnly = !isNew && (ROLE_LEVELS[role] ?? 0) < ROLE_LEVELS.OWNER;

  const [productName, setProductName] = useState('');
  const [ingredients, setIngredients] = useState<EditableIngredient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState('');
  const [yieldQty, setYieldQty] = useState('1');
  const [instructions, setInstructions] = useState('');
  const [linkedProductId, setLinkedProductId] = useState('');
  const [menuItems, setMenuItems] = useState<{ value: string; label: string }[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [availableIngredients, setAvailableIngredients] = useState<Ingredient[]>([]);
  const [ingredientSearch, setIngredientSearch] = useState('');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchRecipe = useCallback(async () => {
    if (!id) return;
    if (isNew) {
      // Create mode: no existing recipe to load — start with an empty form.
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await recipeService.getRecipe(id);
      if (!result?.id) { navigate('/recipes', { replace: true }); return; }
      setProductName(result.product_name ?? '');
      setName(result.name);
      setYieldQty(String(result.yield_quantity ?? 1));
      setInstructions(result.instructions ?? '');
      setLinkedProductId(result.product_id ?? '');
      setIngredients(result.ingredients.map((ing) => ({ ...ing, unit: ing.unit as RecipeUnit })));
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) { navigate('/recipes', { replace: true }); return; }
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load recipe.', variant: 'error' });
    } finally { setIsLoading(false); }
  }, [id, isNew, navigate, toast]);

  useEffect(() => { fetchRecipe(); }, [fetchRecipe]);

  // Fetch menu-item products for the linked product Select.
  // Must use products (menu items) — NOT ingredients — because a recipe's
  // linked product_id references the products table (GitHub issue #77).
  useEffect(() => {
    async function loadMenuItems() {
      try {
        const result = await productService.list({ location_id: selectedLocationId ?? undefined, is_active: 'true', limit: 200 });
        setMenuItems(buildMenuItemOptions(result.data));
      } catch { /* silent */ }
    }
    loadMenuItems();
  }, [selectedLocationId]);

  const fetchIngredients = useCallback(async () => {
    try {
      const result = await recipeService.listIngredients({ location_id: selectedLocationId ?? undefined, is_active: 'true', limit: 200 });
      setAvailableIngredients(result.data);
    } catch { /* silent */ }
  }, [selectedLocationId]);

  const handleSave = useCallback(async () => {
    if (!id) return;
    const plan = buildRecipeSavePlan({ id, name, linkedProductId, yieldQty, instructions, ingredients });
    if (plan.mode === 'invalid') { toast({ title: plan.error, variant: 'error' }); return; }
    setIsSaving(true);
    try {
      if (plan.mode === 'create') {
        const created = await recipeService.createRecipe(plan.payload);
        toast({ title: 'Recipe created', variant: 'success' });
        navigate(`/recipes/${created.id}`, { replace: true });
      } else {
        await recipeService.updateRecipe(plan.id, plan.payload);
        toast({ title: 'Recipe saved', variant: 'success' });
      }
    } catch (err) {
      const fallback = plan.mode === 'create' ? 'Failed to create recipe.' : 'Failed to save.';
      toast({ title: err instanceof ApiError ? err.message : fallback, variant: 'error' });
    } finally { setIsSaving(false); }
  }, [id, name, yieldQty, instructions, ingredients, linkedProductId, navigate, toast]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    setIsDeleting(true);
    try { await recipeService.deleteRecipe(id); navigate('/recipes', { replace: true }); }
    catch (err) { toast({ title: err instanceof ApiError ? err.message : 'Failed to delete.', variant: 'error' }); }
    finally { setIsDeleting(false); setDeleteConfirmOpen(false); }
  }, [id, navigate, toast]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); if (!readOnly) handleSave(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, readOnly]);

  const openAddModal = useCallback(() => { setIngredientSearch(''); setAddModalOpen(true); fetchIngredients(); }, [fetchIngredients]);

  const addIngredient = useCallback((ing: Ingredient) => {
    if (ingredients.some((e) => e.product_id === ing.id)) return;
    setIngredients((prev) => [...prev, { id: `new-${Date.now()}`, product_id: ing.id, product_name: ing.name, quantity: 1, unit: (ing.unit as RecipeUnit) || 'KG', waste_percentage: 0, cost_per_unit: ing.cost ?? 0 }]);
    setAddModalOpen(false);
  }, [ingredients]);

  const removeIngredient = useCallback((pid: string) => { setIngredients((p) => p.filter((i) => i.product_id !== pid)); }, []);
  const updateIngredient = useCallback((pid: string, field: keyof EditableIngredient, value: unknown) => {
    setIngredients((p) => p.map((i) => (i.product_id === pid ? { ...i, [field]: value } : i)));
  }, []);

  const totalCost = useMemo(() => ingredients.reduce((s, i) => s + i.quantity * (1 + i.waste_percentage / 100) * i.cost_per_unit, 0), [ingredients]);
  const costPerPortion = useMemo(() => totalCost / (Number(yieldQty) || 1), [totalCost, yieldQty]);

  const ingColumns: ColumnDef<EditableIngredient>[] = useMemo(() => [
    { key: 'product_name', header: 'Ingredient', render: (_v: unknown, r: EditableIngredient) => <span className="font-semibold text-neutral-800 dark:text-neutral-200">{r.product_name}</span> },
    { key: 'quantity', header: 'Qty', numeric: true, render: (_v: unknown, r: EditableIngredient) => (
      <input type="number" value={r.quantity} onChange={(e) => updateIngredient(r.product_id, 'quantity', Number(e.target.value) || 0)} min="0.001" step="0.01"
        className="w-20 px-2 py-1 rounded-lg bg-neutral-50 dark:bg-neutral-700 text-sm font-mono tabular-nums text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-violet-500 focus:outline-none" aria-label={`Quantity for ${r.product_name}`} />
    )},
    { key: 'unit', header: 'Unit', render: (_v: unknown, r: EditableIngredient) => (
      <Select options={UNIT_OPTIONS} value={r.unit} onChange={(val) => updateIngredient(r.product_id, 'unit', typeof val === 'string' ? val : val[0] ?? 'KG')} />
    )},
    { key: 'waste_percentage', header: 'Waste %', numeric: true, render: (_v: unknown, r: EditableIngredient) => (
      <input type="number" value={r.waste_percentage} onChange={(e) => updateIngredient(r.product_id, 'waste_percentage', Number(e.target.value) || 0)} min="0" max="100"
        className="w-16 px-2 py-1 rounded-lg bg-neutral-50 dark:bg-neutral-700 text-sm font-mono tabular-nums text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-violet-500 focus:outline-none" aria-label={`Waste % for ${r.product_name}`} />
    )},
    { key: 'cost_per_unit', header: 'Cost/Unit', numeric: true, render: (_v: unknown, r: EditableIngredient) => <span className="font-mono tabular-nums text-neutral-600 dark:text-neutral-400">{formatPaise(r.cost_per_unit, currency)}</span> },
    { key: 'line_total', header: 'Total', numeric: true, render: (_v: unknown, r: EditableIngredient) => <span className="font-mono tabular-nums font-bold text-neutral-900 dark:text-neutral-100">{formatPaise(Math.round(r.quantity * (1 + r.waste_percentage / 100) * r.cost_per_unit), currency)}</span> },
    { key: 'actions', header: '', render: (_v: unknown, r: EditableIngredient) => (
      <Button variant="ghost" size="sm" onClick={() => removeIngredient(r.product_id)} className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20" aria-label={`Remove ${r.product_name}`}>
        <TrashIcon />
      </Button>
    )},
  ], [updateIngredient, removeIngredient, currency]);

  const filteredIngredients = useMemo(() => {
    const q = ingredientSearch.toLowerCase();
    return availableIngredients.filter((i) => !ingredients.some((e) => e.product_id === i.id) && i.name.toLowerCase().includes(q));
  }, [availableIngredients, ingredients, ingredientSearch]);

  if (isLoading) {
    return (
      <PageContainer title="Loading..." breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Recipes', href: '/recipes' }, { label: '...' }]}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <div key={i} className={cn(GLASS, 'p-4')}><Skeleton variant="text" width="50%" height={14} className="mb-2" /><Skeleton variant="text" width="30%" height={24} /></div>)}
          </div>
          <div className={cn(GLASS, 'p-5 space-y-4')}><Skeleton variant="text" width="30%" height={20} /><Skeleton variant="rect" width="100%" height={40} /></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={isNew ? 'New Recipe' : name || 'Recipe'}
      subtitle={isNew ? 'Create a recipe' : productName || undefined}
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Recipes', href: '/recipes' }, { label: isNew ? 'New' : name || 'Detail' }]}
      actions={
        <div className="flex items-center gap-3">
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmOpen(true)} className="text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20" aria-label="Delete recipe">
              <TrashIcon /><span className="ml-1.5 hidden sm:inline">Delete</span>
            </Button>
          )}
          {!readOnly && (
            <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : isNew ? 'Create Recipe' : 'Save Recipe'}
            </Button>
          )}
        </div>
      }
    >
      <div className="animate-fade-in space-y-6">
        {!isNew && (
          <div className="rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-pink-50 px-4 py-3 text-sm text-violet-800 dark:border-violet-800 dark:from-violet-900/20 dark:to-pink-900/10 dark:text-violet-200">
            {readOnly
              ? 'This dish uses the same recipe at every location. Only the owner can change it.'
              : 'This dish uses the same recipe at every location. Saving changes it everywhere.'}
          </div>
        )}
        {/* KPI */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiMini label="Food Cost" value={formatPaise(Math.round(totalCost), currency)} tone="danger" />
          <KpiMini label="Cost / Portion" value={formatPaise(Math.round(costPerPortion), currency)} tone="teal" />
          <KpiMini label="Ingredients" value={ingredients.length} tone="violet" />
        </div>

        {/* Recipe Info */}
        <div className={cn(GLASS, 'p-5 space-y-4')}>
          <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Recipe Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Recipe Name</label>
              <Input value={name} onChange={(val) => setName(val)} placeholder="Butter Chicken Recipe" aria-label="Recipe name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Yield (portions)</label>
              <Input value={yieldQty} onChange={(val) => setYieldQty(val)} type="number" placeholder="4" aria-label="Yield quantity" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Linked Menu Item</label>
            <Select
              options={menuItems}
              value={linkedProductId}
              onChange={(val) => setLinkedProductId(typeof val === 'string' ? val : val[0] ?? '')}
              aria-label="Linked menu item"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Instructions</label>
            <TextArea value={instructions} onChange={(val) => setInstructions(val)} placeholder="Marinate chicken for 2 hours..." rows={4} aria-label="Instructions" />
          </div>
        </div>

        {/* Ingredients */}
        <div className={cn(GLASS, 'overflow-hidden')}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100/50 dark:border-neutral-700/50">
            <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Ingredients ({ingredients.length})</h2>
            <Button variant="secondary" size="sm" onClick={openAddModal}><PlusIcon /><span className="ml-1.5">Add Ingredient</span></Button>
          </div>
          {ingredients.length === 0 ? (
            <div className="p-8"><EmptyState variant="inline" icon={<BeakerIcon />} title="No ingredients added" description="Add ingredients to calculate food cost" action={{ label: 'Add Ingredient', onClick: openAddModal }} /></div>
          ) : (
            <div className="overflow-x-auto"><DataTable<EditableIngredient> columns={ingColumns} data={ingredients} /></div>
          )}
          {ingredients.length > 0 && (
            <div className="px-5 py-4 border-t border-neutral-100/50 dark:border-neutral-700/50 flex flex-wrap items-center gap-6 text-sm">
              <div><span className="text-neutral-500 dark:text-neutral-400">Total Cost:</span><span className="ml-2 font-bold font-mono tabular-nums text-neutral-900 dark:text-white">{formatPaise(Math.round(totalCost), currency)}</span></div>
              <div><span className="text-neutral-500 dark:text-neutral-400">Per Portion:</span><span className="ml-2 font-bold font-mono tabular-nums text-neutral-900 dark:text-white">{formatPaise(Math.round(costPerPortion), currency)}</span></div>
            </div>
          )}
        </div>
      </div>

      <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)} title="Add Ingredient" variant="wide">
        <div className="space-y-4 p-1">
          <Input value={ingredientSearch} onChange={(val) => setIngredientSearch(val)} placeholder="Search ingredients..." aria-label="Search ingredients" />
          <div className="max-h-64 overflow-y-auto space-y-1">
            {filteredIngredients.length === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-4">No ingredients found</p>
            ) : filteredIngredients.map((ing) => (
              <button key={ing.id} type="button" onClick={() => addIngredient(ing)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors text-left">
                <div>
                  <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">{ing.name}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">{ing.category ?? 'Uncategorized'} · {ing.unit} · Stock: {ing.stock_quantity}</p>
                </div>
                <span className="text-xs font-mono text-neutral-500 dark:text-neutral-400">{formatPaise(ing.cost ?? 0, currency)}/{ing.unit}</span>
              </button>
            ))}
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={deleteConfirmOpen} title="Delete Recipe?" description={`This will deactivate "${name || 'this recipe'}".`}
        confirmLabel="Delete Recipe" variant="danger" loading={isDeleting} onConfirm={handleDelete} onCancel={() => setDeleteConfirmOpen(false)} />
    </PageContainer>
  );
}

export function RecipeDetailPage() {
  return <ErrorBoundary><RecipeDetailContent /></ErrorBoundary>;
}
